import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { deleteAccount, getScore, getUsage, postBillingPortal } from "../api/client";
import { COGNITO_LOGOUT_URL } from "../auth/config";
import { beginSignOut, useAuth } from "../auth/useAuth";
import { NavBar } from "../components/NavBar";
import { ProfilePreferencesSection } from "../components/ProfilePreferencesSection";
import type { UsageStatus } from "../types/api";

function formatResetDate(iso?: string | null): string {
  if (!iso) return "your next billing date";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * AccountPage — Task 9.8 + account deletion. Two-step model: "Manage
 * subscription" (Stripe portal — cancel billing, keep account) is distinct
 * from the irreversible "Delete account" danger zone below.
 */
export function AccountPage() {
  const auth = useAuth();
  const [portalState, setPortalState] = useState<"idle" | "loading" | "error">("idle");
  const [portalError, setPortalError] = useState<string | null>(null);

  // Delete flow: idle → confirming (type-to-confirm) → deleting → error.
  const [deleteStage, setDeleteStage] = useState<"idle" | "confirming" | "deleting" | "error">("idle");
  const [confirmText, setConfirmText] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // usage-tracking-limits Req 15.8 — show remaining reports subtly in account
  // settings (never a persistent counter on the research surface, Req 15.9).
  const [usage, setUsage] = useState<UsageStatus | null>(null);
  useEffect(() => {
    let cancelled = false;
    getUsage()
      .then((u) => {
        if (!cancelled) setUsage(u);
      })
      .catch(() => {
        /* usage is non-critical — leave the section hidden on failure */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Free tier: surface the report unlocked by the spent trial (Req 3.5/11.4 —
  // permanently accessible). Fetch its address for display; "View" routes home
  // with a handoff flag App.tsx consumes to render the stored report.
  const navigate = useNavigate();
  const [unlockedAddress, setUnlockedAddress] = useState<string | null>(null);
  useEffect(() => {
    if (usage?.tier !== "free" || !usage.pro_trial_score_id) return;
    let cancelled = false;
    getScore(usage.pro_trial_score_id)
      .then((r) => {
        if (!cancelled) setUnlockedAddress(r.address);
      })
      .catch(() => {
        /* no address → just don't show the view block */
      });
    return () => {
      cancelled = true;
    };
  }, [usage]);

  function viewUnlockedReport() {
    try {
      sessionStorage.setItem("pt.viewUnlockedReport", "1");
    } catch {
      /* App falls back to the free view; the report stays reachable there */
    }
    navigate("/");
  }

  async function openPortal() {
    setPortalState("loading");
    setPortalError(null);
    try {
      const { portal_url } = await postBillingPortal();
      window.location.href = portal_url;
    } catch (e) {
      setPortalState("error");
      setPortalError((e as { message?: string }).message || "Could not open the billing portal.");
    }
  }

  async function confirmDelete() {
    setDeleteStage("deleting");
    setDeleteError(null);
    try {
      await deleteAccount();
      // Identity is gone — clear the local session and leave. signOut may
      // throw (the Cognito user no longer exists); ignore and redirect.
      try {
        await auth.signOut();
      } catch {
        /* user already deleted server-side */
      }
      window.location.href = "/?deleted=1";
    } catch (e) {
      setDeleteStage("error");
      setDeleteError((e as { message?: string }).message || "Could not delete your account.");
    }
  }

  async function handleSignOut() {
    // Latch first so RequireAuth doesn't fire signInWithRedirect() the instant
    // signOut() nulls the user — with the hosted session still live, that would
    // silently re-authenticate and just refresh the page.
    beginSignOut();
    try {
      await auth.signOut(); // clears the SPA's local tokens
    } catch {
      /* ignore — we navigate to the hosted logout regardless */
    }
    // Clear the Cognito hosted session cookie and land on the marketing site
    // (COGNITO_LOGOUT_URL's logout_uri = VITE_COGNITO_SIGN_OUT_URI, Req 5.3).
    window.location.assign(COGNITO_LOGOUT_URL);
  }

  return (
    <div className="app">
      <NavBar />
      <main>
        <header className="page-header">
          <h1>Account</h1>
        </header>

        <dl className="account-details">
          <dt>Email</dt>
          <dd>{auth.user?.email || "—"}</dd>
          <dt>Plan</dt>
          <dd>
            {auth.tier === "paid" ? "PriceTilt Pro" : "Free"}
            {auth.tier !== "paid" && (
              <Link to="/upgrade" className="account-upgrade-link">
                {" "}— upgrade
              </Link>
            )}
          </dd>
        </dl>

        <ProfilePreferencesSection />

        {usage && (
          <section className="account-section account-usage">
            <h2>Report usage</h2>
            {usage.tier === "pro" && usage.subscription_ended ? (
              <div className="account-subscription-ended">
                <p className="account-usage-line">
                  Your Pro subscription has ended. Your previous reports are still
                  accessible.
                </p>
                <Link to="/upgrade" className="resubscribe-btn">
                  Resubscribe to Pro
                </Link>
              </div>
            ) : usage.tier === "pro" ? (
              <>
                <p className="account-usage-line">
                  {usage.reports_remaining ?? 0} of {usage.reports_limit} Pro
                  reports remaining this month
                  {usage.period_resets_at
                    ? ` · resets ${formatResetDate(usage.period_resets_at)}`
                    : ""}
                  .
                </p>
                {(usage.reports_remaining ?? 0) <= 3 && (
                  <p className="account-usage-low" role="status">
                    You have {usage.reports_remaining ?? 0} Pro reports remaining
                    this month. Resets {formatResetDate(usage.period_resets_at)}.
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="account-usage-line">
                  {(usage.pro_trial_remaining ?? 0) > 0
                    ? "You have 1 free Pro report to try — spend it on any property from its page."
                    : "You’ve used your free Pro report. Upgrade to PriceTilt Pro for more verified reports."}
                </p>
                {unlockedAddress && (
                  <div className="account-unlocked-report">
                    <p className="account-usage-line">
                      Your unlocked Pro report:{" "}
                      <strong>{unlockedAddress}</strong> — yours to revisit
                      anytime, included with your free Pro report.
                    </p>
                    <button
                      type="button"
                      className="submit"
                      onClick={viewUnlockedReport}
                    >
                      View your Pro report
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        <button
          type="button"
          className="submit account-signout-btn"
          onClick={() => void handleSignOut()}
        >
          Sign out
        </button>

        {auth.tier === "paid" && (
          <section className="account-section">
            <h2>Subscription</h2>
            <p>
              Update your payment method, view invoices, or cancel — securely
              on Stripe.
            </p>
            <button
              type="button"
              className="submit"
              onClick={() => void openPortal()}
              disabled={portalState === "loading"}
            >
              {portalState === "loading" ? "Opening…" : "Manage subscription"}
            </button>
            {portalError && (
              <p className="error" role="alert">
                {portalError}
              </p>
            )}
          </section>
        )}

        <section className="account-section">
          <h2>Have an invite code?</h2>
          <p>
            If your agent gave you a PriceTilt Pro invite code, redeem it here.
          </p>
          {/* Redeem call (POST /invites/redeem) is a follow-up (Task 9.8 stub). */}
          <div className="invite-redeem-row">
            <input type="text" placeholder="INVITE-XXXX-XXXX" aria-label="Invite code" />
            <button type="button" className="submit" disabled title="Coming soon">
              Redeem
            </button>
          </div>
        </section>

        <section className="account-section account-danger">
          <h2>Delete account</h2>
          <p>
            Permanently delete your account and all associated data — saved
            properties, alerts, and chat history. This can&rsquo;t be undone.
            {auth.tier === "paid" && " Any active subscription is cancelled."}
          </p>
          {deleteStage === "idle" && (
            <button
              type="button"
              className="danger-btn"
              onClick={() => {
                setConfirmText("");
                setDeleteStage("confirming");
              }}
            >
              Delete account
            </button>
          )}
          {(deleteStage === "confirming" || deleteStage === "deleting" || deleteStage === "error") && (
            <div className="account-delete-confirm">
              <label htmlFor="delete-confirm">
                Type <strong>DELETE</strong> to confirm:
              </label>
              <input
                id="delete-confirm"
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                autoComplete="off"
                disabled={deleteStage === "deleting"}
              />
              <div className="account-delete-actions">
                <button
                  type="button"
                  className="danger-btn"
                  disabled={confirmText !== "DELETE" || deleteStage === "deleting"}
                  onClick={() => void confirmDelete()}
                >
                  {deleteStage === "deleting" ? "Deleting…" : "Permanently delete"}
                </button>
                <button
                  type="button"
                  className="sign-out-link"
                  disabled={deleteStage === "deleting"}
                  onClick={() => setDeleteStage("idle")}
                >
                  Cancel
                </button>
              </div>
              {deleteError && (
                <p className="error" role="alert">
                  {deleteError}
                </p>
              )}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
